import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type {
  NullableTypographyVariant,
  TypographyVariant,
} from '@sorare/core/src/atoms/typography';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { CardDescriptionFromProps } from '@sorare/core/src/components/token/CardDescriptionFromProps';
import { TokenMetas } from '@sorare/core/src/components/token/TokenMetas';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import type { Color } from '@sorare/core/src/style/types';

import type { CardDescription_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  card: CardDescription_anyCard;
  separator?: string;
  Title?: TypographyVariant;
  titleColor?: Color;
  Details?: NullableTypographyVariant;
  detailsColor?: Color;
  withoutLink?: boolean;
  row?: boolean;
  bonus?: ReactNode;
};

export const CardDescription = ({
  card,
  Title,
  titleColor = 'var(--c-white)',
  separator = ' \u2013 ',
  Details = Title,
  detailsColor = titleColor,
  withoutLink = false,
  bonus,
  ...styleProps
}: Props) => {
  const { anyPlayer: player, sport } = card;

  const TokenMetasDatas = useMemo(() => {
    return <TokenMetas card={card} separator={separator} />;
  }, [card, separator]);

  const { generateSportContextPath } = useSportContext();

  return (
    <CardDescriptionFromProps
      displayName={player.displayName}
      path={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
        params: { slug: player.slug },
        sport,
      })}
      description={TokenMetasDatas}
      withoutLink={withoutLink}
      Details={Details}
      detailsColor={detailsColor}
      Title={Title || BodyM}
      titleColor={titleColor}
      bonus={bonus}
      {...styleProps}
    />
  );
};

CardDescription.fragments = {
  anyCard: gql`
    fragment CardDescription_anyCard on AnyCardInterface {
      slug
      sport
      anyPlayer {
        slug
        displayName
      }
      ...TokenMetas_anyCard
    }
    ${TokenMetas.fragments.anyCard}
  ` as TypedDocumentNode<CardDescription_anyCard>,
};
